import { useMemo, useState } from 'react';
import { StediPayerSearch, type StediPayerSearchOption } from '@features/payer-search';
import { useAdminPayers } from '@features/payer-search/store/hooks';
import { useDebounce } from '@hooks/useDebounce';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';

import type { PayerEnrollmentStatus, PayerRow } from './types';

interface IntakeAdminPayerSearchProps {
  existingRows: PayerRow[];
  onAdd: (row: PayerRow) => void;
}

export function IntakeAdminPayerSearch({ existingRows, onAdd }: IntakeAdminPayerSearchProps) {
  const [search, setSearch] = useState('');
  const [enrollmentStatus, setEnrollmentStatus] =
    useState<PayerEnrollmentStatus>('already_enrolled');
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const debouncedSearch = useDebounce(search.trim(), 300);
  const { payers, isLoading, isFetching, isError } = useAdminPayers(
    { search: debouncedSearch.length >= 2 ? debouncedSearch : null, perPage: 50 },
    { skip: debouncedSearch.length < 2 },
  );
  const options = useMemo<StediPayerSearchOption[]>(
    () =>
      payers.map(payer => ({
        avatarUrl: payer.avatar_url,
        displayName: payer.display_name,
        payerId: payer.primary_payer_id,
        stediId: payer.stedi_id,
      })),
    [payers],
  );
  const payerByStediId = useMemo(
    () => new Map(payers.map(payer => [payer.stedi_id, payer])),
    [payers],
  );
  const selectPayer = (option: StediPayerSearchOption | null) => {
    const payer = option ? (payerByStediId.get(option.stediId) ?? null) : null;
    if (!payer) return;
    const payerId = payer.primary_payer_id?.trim();
    if (!payerId) {
      setSelectionError('This payer does not have a primary payer ID and cannot be added.');
      return;
    }
    if (existingRows.some(row => row.payerId === payerId)) {
      setSelectionError('That payer is already in the practice roster.');
      return;
    }
    onAdd({
      displayName: payer.display_name,
      enrollmentStatus,
      jotbill: false,
      jotcred: false,
      payerId,
      rowId: payerId,
    });
    setSearch('');
    setSelectionError(null);
  };

  return (
    <>
      <FormControl size="small" sx={{ mb: 1, minWidth: 220 }}>
        <InputLabel id="payer-enrollment-section-label">Add payer to</InputLabel>
        <Select
          label="Add payer to"
          labelId="payer-enrollment-section-label"
          onChange={event => setEnrollmentStatus(event.target.value)}
          value={enrollmentStatus}
        >
          <MenuItem value="already_enrolled">Already enrolled</MenuItem>
          <MenuItem value="wants_to_enroll">Want to enroll</MenuItem>
        </Select>
      </FormControl>
      <StediPayerSearch
        error={isError}
        helperText="Admin only · select a payer to add it to the selected section"
        inputValue={search}
        loading={isLoading || isFetching}
        onInputChange={setSearch}
        onSelect={selectPayer}
        options={options}
        placeholder="Search STEDI payers to add (e.g., Ambetter, Lucet, Tricare…)"
      />
      {selectionError && (
        <Box role="alert" sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
          {selectionError}
        </Box>
      )}
    </>
  );
}
